import { useEffect, useMemo, useState } from "react";

import { useCurrentUser } from "@/lib/hooks/use-current-user";
import { getPreviewEmailSignature } from "@/lib/utils/email-signature";
import { isLovablePreview } from "@/lib/utils/environment";

/**
 * Returns the current user's email signature for the active mode.
 *
 * - Preview mode: reads `localStorage["coffee-preview-email-signature"]` and
 *   updates reactively when the Settings page dispatches
 *   `preview-email-signature-changed` (or when another tab triggers `storage`).
 * - Production mode: reads `currentUser.emailSignature` from the backend.
 *
 * Centralizes the dual-mode branch so callers don't reimplement it. Used by
 * the campaign builder preview pane and `ComposeEmailDialog`.
 */
export function useEffectiveEmailSignature(): string {
  const preview = isLovablePreview();
  const { currentUser } = useCurrentUser();

  const [previewSignature, setPreviewSignature] = useState<string>(() =>
    preview ? getPreviewEmailSignature() : "",
  );

  useEffect(() => {
    if (!preview) return;
    const handler = () => setPreviewSignature(getPreviewEmailSignature());
    window.addEventListener("preview-email-signature-changed", handler);
    window.addEventListener("storage", handler);
    return () => {
      window.removeEventListener("preview-email-signature-changed", handler);
      window.removeEventListener("storage", handler);
    };
  }, [preview]);

  return useMemo(() => {
    if (preview) return previewSignature;
    return currentUser?.emailSignature || "";
  }, [preview, previewSignature, currentUser]);
}
